import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { sanitizeSearchQuery } from '@/utils/sanitize';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Search, Loader2, FileText } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

export interface InvoiceableJob {
  id: string;
  job_number: string;
  customer_id: string | null;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string;
  customer_address: string | null;
  customer_suburb?: string | null;
  customer_state?: string | null;
  customer_postcode?: string | null;
  work_summary?: string | null;
  invoice_total: number | null;
  total_parts_cost: number;
  toll_cost: number;
  job_date: string | null;
  invoice_number?: string | null;
  invoice_id?: string | null;
}

interface SelectJobForInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (job: InvoiceableJob) => void;
  /** Called when the user picks a job that already carries an invoice, so the existing one can be opened. */
  onOpenExisting?: (invoiceId: string) => void;
}

/**
 * Every invoice must belong to a job. This picker lists completed jobs so the
 * user chooses one first; the invoice itself is then built from that job.
 */
export function SelectJobForInvoiceDialog({ open, onOpenChange, onSelect, onOpenExisting }: SelectJobForInvoiceDialogProps) {
  const { user } = useAuth();
  const [search, setSearch] = useState('');

  const { data: jobs = [], isLoading } = useQuery({
    queryKey: ['invoiceable-jobs', search],
    enabled: open && !!user,
    queryFn: async (): Promise<InvoiceableJob[]> => {
      let query = supabase
        .from('jobs')
        .select(
          'id, job_number, customer_id, customer_name, customer_email, customer_phone, customer_address, ' +
          'customer_suburb, customer_state, customer_postcode, work_summary, invoice_total, ' +
          'total_parts_cost, toll_cost, job_date',
        )
        .eq('status', 'completed')
        .order('job_date', { ascending: false, nullsFirst: false })
        .limit(25);

      const safe = sanitizeSearchQuery(search);
      if (safe.length > 0) {
        query = query.or(
          `customer_name.ilike.%${safe}%,customer_phone.ilike.%${safe}%,job_number.ilike.%${safe}%`,
        );
      }

      const { data, error } = await query;
      if (error) throw error;
      const rows = (data ?? []) as unknown as InvoiceableJob[];
      if (rows.length === 0) return rows;

      // Flag jobs that already carry an invoice so they can't be invoiced twice;
      // keep the id so the row can open that invoice instead of dead-ending
      const { data: existing } = await supabase
        .from('invoices')
        .select('id, job_id, invoice_number')
        .in('job_id', rows.map(j => j.id))
        .neq('status', 'cancelled');
      const invoiced = new Map((existing ?? []).map(i => [i.job_id, i]));

      return rows.map(j => ({
        ...j,
        invoice_number: invoiced.get(j.id)?.invoice_number ?? null,
        invoice_id: invoiced.get(j.id)?.id ?? null,
      }));
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            New Invoice
          </DialogTitle>
          <DialogDescription>
            Choose the completed job this invoice is for. Invoices are always raised against a job.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search by job number, customer or phone"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>

        <div className="max-h-[50vh] overflow-y-auto rounded-md border divide-y">
          {isLoading ? (
            <div className="flex items-center justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : jobs.length === 0 ? (
            <p className="py-10 text-center text-sm text-muted-foreground">
              {search ? 'No completed jobs match that search.' : 'No completed jobs to invoice yet.'}
            </p>
          ) : (
            jobs.map(job => {
              const alreadyInvoiced = !!job.invoice_id;
              // An invoiced job can't be invoiced again, but clicking it opens the
              // existing invoice so the user can edit that one instead
              const canOpenExisting = alreadyInvoiced && !!onOpenExisting;
              return (
                <button
                  key={job.id}
                  type="button"
                  disabled={alreadyInvoiced && !canOpenExisting}
                  title={alreadyInvoiced ? 'This job is already invoiced. Click to open the invoice.' : undefined}
                  onClick={() => {
                    if (alreadyInvoiced) {
                      if (canOpenExisting) onOpenExisting(job.invoice_id as string);
                      return;
                    }
                    onSelect(job);
                  }}
                  className={cn(
                    'flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-60',
                    alreadyInvoiced && 'opacity-70',
                  )}
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{job.job_number}</span>
                      {alreadyInvoiced && (
                        <Badge variant="secondary">Invoiced · {job.invoice_number}{canOpenExisting ? ' · Open' : ''}</Badge>
                      )}
                    </div>
                    <div className="truncate text-sm text-muted-foreground">
                      {job.customer_name}
                      {job.customer_phone ? ` · ${job.customer_phone}` : ''}
                      {job.job_date ? ` · ${format(new Date(job.job_date), 'd MMM yyyy')}` : ''}
                    </div>
                  </div>
                  <span className="shrink-0 text-sm tabular-nums">
                    ${Number(job.invoice_total ?? 0).toFixed(2)}
                  </span>
                </button>
              );
            })
          )}
        </div>

        <div className="flex justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
